import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Type,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Palette,
  Minus,
  Plus,
  MoreHorizontal
} from 'lucide-react';
import { FontSelector } from './FontSelector';
import { AdvancedColorPicker } from './AdvancedColorPicker';

export interface TextStyle {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline' | 'line-through';
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  color?: string;
  lineHeight?: number;
  letterSpacing?: number;
}

interface TextFormattingPanelProps {
  textStyle: TextStyle;
  onChange: (style: TextStyle) => void;
  className?: string;
}

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72, 96];

const FONT_WEIGHTS = [
  { value: '100', label: 'Thin' },
  { value: '200', label: 'Extra Light' },
  { value: '300', label: 'Light' },
  { value: '400', label: 'Regular' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semi Bold' },
  { value: '700', label: 'Bold' },
  { value: '800', label: 'Extra Bold' },
  { value: '900', label: 'Black' }
];

export const TextFormattingPanel: React.FC<TextFormattingPanelProps> = ({
  textStyle,
  onChange,
  className = ''
}) => {
  const [showFontSelector, setShowFontSelector] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const updateStyle = useCallback((updates: Partial<TextStyle>) => {
    onChange({ ...textStyle, ...updates });
  }, [textStyle, onChange]);

  const handleFontSizeChange = useCallback((delta: number) => {
    const currentSize = textStyle.fontSize || 16;
    const newSize = Math.max(8, Math.min(200, currentSize + delta));
    updateStyle({ fontSize: newSize });
  }, [textStyle.fontSize, updateStyle]);

  const handleLineHeightChange = useCallback((delta: number) => {
    const currentLineHeight = textStyle.lineHeight || 1.4;
    const newLineHeight = Math.max(0.8, Math.min(3, currentLineHeight + delta));
    updateStyle({ lineHeight: Math.round(newLineHeight * 10) / 10 });
  }, [textStyle.lineHeight, updateStyle]);

  const handleLetterSpacingChange = useCallback((delta: number) => {
    const currentSpacing = textStyle.letterSpacing || 0;
    const newSpacing = Math.max(-2, Math.min(10, currentSpacing + delta));
    updateStyle({ letterSpacing: Math.round(newSpacing * 10) / 10 });
  }, [textStyle.letterSpacing, updateStyle]);

  return (
    <div className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
      {/* Font & Color */}
      <div className="p-4 border-b border-gray-200">
        <div className="text-sm font-medium text-gray-700 mb-3">Font & Color</div>
        <div className="grid grid-cols-2 gap-3">
          {/* Font Family */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Font Family</label>
            <button
              onClick={() => setShowFontSelector(true)}
              className="w-full p-2 text-left border border-gray-300 rounded-lg hover:border-blue-300 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span
                  className="text-sm truncate"
                  style={{ fontFamily: textStyle.fontFamily || 'Inter, sans-serif' }}
                >
                  {(textStyle.fontFamily || 'Inter').split(',')[0]}
                </span>
                <Type className="w-3 h-3 text-gray-400 flex-shrink-0" />
              </div>
            </button>
          </div>

          {/* Text Color */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Color</label>
            <button
              onClick={() => setShowColorPicker(true)}
              className="w-full p-2 border border-gray-300 rounded-lg hover:border-blue-300 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <div
                  className="w-4 h-4 rounded border border-gray-200 flex-shrink-0"
                  style={{ backgroundColor: textStyle.color || '#000000' }}
                />
                <span className="text-xs truncate">{textStyle.color || '#000000'}</span>
                <Palette className="w-3 h-3 text-gray-400 flex-shrink-0 ml-auto" />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Font Size & Weight */}
      <div className="p-4 border-b border-gray-200">
        <div className="grid grid-cols-2 gap-4">
          {/* Font Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleFontSizeChange(-1)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <Minus className="w-4 h-4" />
              </button>
              <select
                value={textStyle.fontSize || 16}
                onChange={(e) => updateStyle({ fontSize: parseInt(e.target.value) })}
                className="flex-1 p-1 border border-gray-300 rounded text-center"
              >
                {FONT_SIZES.map(size => (
                  <option key={size} value={size}>{size}px</option>
                ))}
              </select>
              <button
                onClick={() => handleFontSizeChange(1)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Font Weight */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Weight</label>
            <select
              value={textStyle.fontWeight || '400'}
              onChange={(e) => updateStyle({ fontWeight: e.target.value as TextStyle['fontWeight'] })}
              className="w-full p-1 border border-gray-300 rounded"
            >
              {FONT_WEIGHTS.map(weight => (
                <option key={weight.value} value={weight.value}>{weight.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Text Style Buttons */}
      <div className="p-4 border-b border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">Style</label>
        <div className="flex space-x-1">
          <button
            onClick={() => updateStyle({ 
              fontWeight: textStyle.fontWeight === 'bold' ? 'normal' : 'bold' 
            })}
            className={`p-2 rounded transition-colors ${
              textStyle.fontWeight === 'bold' || textStyle.fontWeight === '700'
                ? 'bg-blue-100 text-blue-700'
                : 'hover:bg-gray-100'
            }`}
          >
            <Bold className="w-4 h-4" />
          </button>
          <button
            onClick={() => updateStyle({ 
              fontStyle: textStyle.fontStyle === 'italic' ? 'normal' : 'italic' 
            })}
            className={`p-2 rounded transition-colors ${
              textStyle.fontStyle === 'italic'
                ? 'bg-blue-100 text-blue-700'
                : 'hover:bg-gray-100'
            }`}
          >
            <Italic className="w-4 h-4" />
          </button>
          <button
            onClick={() => updateStyle({ 
              textDecoration: textStyle.textDecoration === 'underline' ? 'none' : 'underline' 
            })}
            className={`p-2 rounded transition-colors ${
              textStyle.textDecoration === 'underline'
                ? 'bg-blue-100 text-blue-700'
                : 'hover:bg-gray-100'
            }`}
          >
            <Underline className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Text Alignment */}
      <div className="p-4 border-b border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">Alignment</label>
        <div className="flex space-x-1">
          <button
            onClick={() => updateStyle({ textAlign: 'left' })}
            className={`p-2 rounded transition-colors ${
              textStyle.textAlign === 'left' || !textStyle.textAlign
                ? 'bg-blue-100 text-blue-700'
                : 'hover:bg-gray-100'
            }`}
          >
            <AlignLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => updateStyle({ textAlign: 'center' })}
            className={`p-2 rounded transition-colors ${
              textStyle.textAlign === 'center'
                ? 'bg-blue-100 text-blue-700'
                : 'hover:bg-gray-100'
            }`}
          >
            <AlignCenter className="w-4 h-4" />
          </button>
          <button
            onClick={() => updateStyle({ textAlign: 'right' })}
            className={`p-2 rounded transition-colors ${
              textStyle.textAlign === 'right'
                ? 'bg-blue-100 text-blue-700'
                : 'hover:bg-gray-100'
            }`}
          >
            <AlignRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => updateStyle({ textAlign: 'justify' })}
            className={`p-2 rounded transition-colors ${
              textStyle.textAlign === 'justify'
                ? 'bg-blue-100 text-blue-700'
                : 'hover:bg-gray-100'
            }`}
          >
            <AlignJustify className="w-4 h-4" />
          </button>
        </div>
      </div>



      {/* Advanced Options */}
      <div className="p-4">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center justify-between w-full text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          Advanced Options
          <MoreHorizontal className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
        </button>
        
        {showAdvanced && (
          <div className="mt-4 space-y-4">
            {/* Line Height */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Line Height: {textStyle.lineHeight || 1.4}
              </label>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleLineHeightChange(-0.1)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="range"
                  min="0.8"
                  max="3"
                  step="0.1"
                  value={textStyle.lineHeight || 1.4}
                  onChange={(e) => updateStyle({ lineHeight: parseFloat(e.target.value) })}
                  className="flex-1"
                />
                <button
                  onClick={() => handleLineHeightChange(0.1)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Letter Spacing */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Letter Spacing: {textStyle.letterSpacing || 0}px
              </label>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleLetterSpacingChange(-0.1)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="range"
                  min="-2"
                  max="10"
                  step="0.1"
                  value={textStyle.letterSpacing || 0}
                  onChange={(e) => updateStyle({ letterSpacing: parseFloat(e.target.value) })}
                  className="flex-1"
                />
                <button
                  onClick={() => handleLetterSpacingChange(0.1)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Font Selector Modal - Rendered as Portal */}
      {showFontSelector && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9997]"
          onClick={() => setShowFontSelector(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <FontSelector
              selectedFont={textStyle.fontFamily || 'Inter'}
              onFontSelect={(fontFamily) => {
                updateStyle({ fontFamily });
                setShowFontSelector(false);
              }}
              onClose={() => setShowFontSelector(false)}
              className="w-96 max-w-[90vw] max-h-[90vh]"
            />
          </div>
        </div>,
        document.body
      )}

      {/* Color Picker Modal - Rendered as Portal */}
      {showColorPicker && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9996]"
          onClick={() => setShowColorPicker(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <AdvancedColorPicker
              color={textStyle.color || '#000000'}
              onChange={(color) => updateStyle({ color })}
              onClose={() => setShowColorPicker(false)}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
